import React, { useEffect, useRef, useState } from 'react';
import { FiAlertCircle, FiCheckCircle, FiDownload, FiLoader, FiRefreshCw, FiX } from 'react-icons/fi';
import { Button } from './ui/Button';
import { useAuth } from '../contexts/AuthContext';

interface BatchJobProgressProps {
  batchId: string | null;
  onClose: () => void;
  onComplete?: (resultUrl?: string) => void;
}

interface BatchJobStatus {
  id: string;
  status: string;
  total_items: number;
  processed_items: number;
  success_count: number;
  error_count: number;
  result_url?: string;
  error_details?: Array<{ id: string; error: string }>;
  envelope_result_url?: string;
}

const parseDownloadFilename = (contentDisposition?: string | null, fallbackName?: string) => {
  if (!contentDisposition) return fallbackName;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (plainMatch?.[1]) {
    return plainMatch[1];
  }

  return fallbackName;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const triggerBrowserDownload = (href: string, fallbackName: string) => {
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', fallbackName);
  link.setAttribute('target', '_blank');
  link.setAttribute('rel', 'noopener');
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const BatchJobProgress: React.FC<BatchJobProgressProps> = ({
  batchId,
  onClose,
  onComplete,
}) => {
  const { token, currentBusiness, isCookieMode, API_BASE_URL } = useAuth();
  const businessId = currentBusiness?.id;
  const [jobStatus, setJobStatus] = useState<BatchJobStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionWarning, setConnectionWarning] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const hasLoadedStatus = useRef(false);
  const completionNotified = useRef(false);
  const latestJobStatus = useRef<BatchJobStatus | null>(null);

  useEffect(() => {
    hasLoadedStatus.current = false;
    completionNotified.current = false;
    latestJobStatus.current = null;
    setJobStatus(null);
    setError(null);
    setConnectionWarning(null);
    setIsLoading(true);
  }, [batchId]);

  useEffect(() => {
    if (!batchId) return;

    let interval: NodeJS.Timeout | null = null;

    const pollStatus = async () => {
      try {
        if (!businessId) throw new Error('Business context required');
        if (!isCookieMode && !token) throw new Error('Authentication required');

        const headers: Record<string, string> = {};
        if (!isCookieMode && token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/batch/${batchId}`, {
          headers,
          credentials: isCookieMode ? 'include' : 'omit',
          cache: 'no-store',
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch job status: ${response.status} ${errorText}`);
        }

        const status: BatchJobStatus = await response.json();
        latestJobStatus.current = status;
        setJobStatus(status);
        setIsLoading(false);
        setError(null);
        setConnectionWarning(null);
        hasLoadedStatus.current = true;

        if (!completionNotified.current && ['completed', 'completed_with_errors'].includes(status.status)) {
          completionNotified.current = true;
          onComplete?.(status.result_url);
        }

        if (['completed', 'completed_with_errors', 'failed'].includes(status.status) && interval) {
          clearInterval(interval);
          interval = null;
        }
      } catch (err: any) {
        const message = err?.message || 'Failed to fetch status';
        setIsLoading(false);
        if (hasLoadedStatus.current || latestJobStatus.current) {
          setConnectionWarning(message);
        } else {
          setError(message);
        }
      }
    };

    void pollStatus();
    interval = setInterval(pollStatus, 2500);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [API_BASE_URL, batchId, businessId, isCookieMode, onComplete, reloadToken, token]);

  if (!batchId) return null;

  const total = typeof jobStatus?.total_items === 'number' ? jobStatus.total_items : undefined;
  const processed = typeof jobStatus?.processed_items === 'number' ? jobStatus.processed_items : undefined;
  const isPreparing = !!jobStatus && ['pending', 'processing'].includes(jobStatus.status) && (!total || total <= 0);
  const percent = total && total > 0 ? Math.round(((processed || 0) / total) * 100) : (jobStatus?.status === 'completed' ? 100 : 0);

  const getStatusIcon = () => {
    if (isLoading) return <FiLoader className="w-6 h-6 animate-spin" />;
    switch (jobStatus?.status) {
      case 'completed':
        return <FiCheckCircle className="w-6 h-6 text-green-500" />;
      case 'completed_with_errors':
        return <FiAlertCircle className="w-6 h-6 text-yellow-500" />;
      case 'failed':
        return <FiX className="w-6 h-6 text-red-500" />;
      default:
        return <FiLoader className="w-6 h-6 animate-spin" />;
    }
  };

  const getStatusText = () => {
    if (isLoading) return 'Checking status...';
    if (isPreparing) return 'Preparing batch...';
    switch (jobStatus?.status) {
      case 'pending':
        return 'Job queued...';
      case 'processing':
        return 'Processing...';
      case 'completed':
        return 'Completed successfully!';
      case 'completed_with_errors':
        return 'Completed with errors';
      case 'failed':
        return 'Failed';
      default:
        return 'Unknown status';
    }
  };

  const handleRetry = () => {
    setError(null);
    setConnectionWarning(null);
    setIsLoading(true);
    setReloadToken((prev) => prev + 1);
  };

  const downloadBatchFile = async (downloadPath: string, fallbackName: string) => {
    try {
      if (isCookieMode) {
        triggerBrowserDownload(`${API_BASE_URL}${downloadPath}`, fallbackName);
        return;
      }

      const headers: Record<string, string> = {};
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}${downloadPath}`, {
        headers,
        credentials: isCookieMode ? 'include' : 'omit',
        cache: 'no-store',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Download failed: ${response.status} ${errorText}`);
      }

      const fileName = parseDownloadFilename(
        response.headers.get('content-disposition'),
        fallbackName
      ) || fallbackName;

      const blob = await response.blob();
      downloadBlob(blob, fileName);
    } catch (err: any) {
      setError(err?.message || 'Failed to download batch file');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Batch Operation Progress</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {error ? (
          <div className="space-y-4">
            <div className="text-red-500 mb-4">
              <FiAlertCircle className="w-5 h-5 inline mr-2" />
              {error}
            </div>
            <div className="flex justify-end space-x-2">
              <Button onClick={handleRetry} variant="secondary" size="sm">
                <FiRefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
              <Button onClick={onClose} variant="secondary" size="sm">Close</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              {getStatusIcon()}
              <span className="text-gray-700 dark:text-gray-300">{getStatusText()}</span>
            </div>

            {connectionWarning && (
              <div className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800 dark:border-yellow-900/40 dark:bg-yellow-950/30 dark:text-yellow-200">
                <FiAlertCircle className="mr-2 inline h-4 w-4" />
                Connection issue while refreshing progress. Retrying automatically.
              </div>
            )}

            {jobStatus && (
              <div className="space-y-3">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${percent}%` }} />
                </div>

                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex justify-between">
                    <span>Processed:</span>
                    <span>
                      {total && total > 0
                        ? `${processed ?? 0} / ${total}`
                        : (isPreparing ? 'Preparing batch...' : `${processed ?? 'N/A'} / ${total ?? 'N/A'}`)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Success:</span>
                    <span className="text-green-600">{jobStatus.success_count ?? 0}</span>
                  </div>
                  {jobStatus.error_count > 0 && (
                    <div className="flex justify-between">
                      <span>Errors:</span>
                      <span className="text-red-600">{jobStatus.error_count}</span>
                    </div>
                  )}
                </div>

                {jobStatus.result_url && (
                  <div className="pt-2">
                    <Button
                      onClick={() => {
                        void downloadBatchFile(`/batch/download/${batchId}`, `batch-invoices-${batchId}.pdf`);
                      }}
                      className="w-full"
                    >
                      <FiDownload className="w-4 h-4 mr-2" />
                      Download Invoices PDF
                    </Button>
                  </div>
                )}

                {jobStatus.envelope_result_url && (
                  <div className="pt-2">
                    <Button
                      onClick={() => {
                        void downloadBatchFile(`/batch/download/${batchId}/envelopes`, `batch-envelopes-${batchId}.pdf`);
                      }}
                      className="w-full"
                    >
                      <FiDownload className="w-4 h-4 mr-2" />
                      Download Envelopes PDF
                    </Button>
                  </div>
                )}

                {jobStatus.error_details && jobStatus.error_details.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Errors:</h4>
                    <div className="max-h-32 overflow-y-auto text-xs text-red-600">
                      {jobStatus.error_details.map((item, index) => (
                        <div key={index} className="mb-1">
                          <strong>Item {item.id}:</strong> {item.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!error && (
          <div className="mt-6 flex justify-end">
            <Button onClick={onClose} variant="secondary" size="sm">Close</Button>
          </div>
        )}
      </div>
    </div>
  );
};
